// The one composer (mixed-posts spec): any mix of photos, kept videos and links in one ordered list,
// for GC create / edit / import and the personal wall (Figma web Composer 4260:3761 / 4260:3849 /
// 4260:4017, personal modal 4260:4451, Composer body 4258:2229). On a GC wall it fills the right
// panel (`inPanel`); the personal wall, and a GC edit opened from a dialog, use the centred modal.
// Typed v2 posts (News, Event — Figma eWr6sqSGfDufrRNUMjbtK4 2762:1307 / 2972:12343) are created via POST v2
// and edited via PUT v2 with the same editor (3a R5); their type never changes.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FolderUp, SquarePen, X } from "lucide-react";
import {
  ContentError, createEvent, createOnMyWall, createPost, createPostV2, getPostV2, resolveLink, updatePost, updatePostV2,
  type PostInput, type PostInputV2, type PostItemInput, type PostV2, type WallPost,
} from "../../api/content";
import { SvgTooLargeError, uploadAudio, uploadImage } from "../../api/upload";
import { invalidateNewsPosted, invalidatePersonalFeeds, upsertPost } from "../../hooks/useWallFeed";
import { uploadQueue, useUploadQueueStore } from "../../store/uploadQueue";
import {
  claimDraft, enqueueWallVideo, isDraftQueued, queueWallPost, releaseDraft, releaseWallVideos, useWallVideoPostStore, videoSendOf,
} from "../../services/wallVideoPost";
import { clampInsertion, codePointCount } from "./codePoints";
import { uploadTitle } from "./uploadRow";
import ComposerItemList from "./ComposerItemList";
import {
  AUDIO_TYPES, MAX_ITEMS, MEDIA_TYPES, PHOTO_TYPES, applyResolved, blockerOf, canQueuePost, composerRules, deriveKind, editTitle, filesFromPicked,
  isTypedTarget, itemsFromPost, newLink, patchItem, prefillText, resetTitle, toPostItems, withVideoSend,
  type ComposerItem, type LinkItem, type WallTarget,
} from "./composerModel";
import { useAppOverlay } from "../../store/appOverlayStore";
import { useDockAvoid, useDockAvoidStore } from "../../store/dockAvoidStore";
import { useComposerDraftStore } from "../../store/composerDraftStore";
import LiveConfirmDialog from "../stream/LiveConfirmDialog";
import { isVideoItem } from "../../player";
import { contentErrorText } from "./contentErrorText";
import EventFields from "../events/EventFields";
import { browserTimezone, eventDraftOf, eventErrors, newEventDraft, profileLinkOf, toEventInput, type EventDraft } from "../events/eventDraft";

interface Props {
  /** Which wall the post goes to; its rules shape the form (title and links on GC walls only). */
  target: WallTarget;
  existing?: WallPost | null;
  /** Import: the caller's post to copy. Ignored when `existing` is set. */
  source?: WallPost | null;
  resolveDelayMs?: number;
  /** Render inside the host panel (GC wall, Figma 4260:3761) instead of a centred modal. Ignored on the personal wall. */
  inPanel?: boolean;
  /**
   * New event only (3b R4 "Create here"): the map point to start from. Seeds the draft's coords, so the place-name
   * field is enabled and the post carries the location. Ignored on edit.
   */
  initialLocation?: { lat: number; lng: number } | null;
  onClose(): void;
  onSaved(post: WallPost): void;
}

const MAX_TITLE = 120;

/**
 * An event edit needs the post's `event` facts: a copy without them (a v1 view, a stale cache) is read again
 * through GET v2 first, so the editor never opens on a blank event (which would overwrite the stored one).
 */
export default function PostEditorModal(props: Props) {
  const { t } = useTranslation();
  const ex = props.existing as PostV2 | null | undefined;
  const needsEvent = props.target.type === "event" && ex != null && ex.event == null;
  const full = useQuery({
    queryKey: ["postEditV2", ex?.id ?? null],
    queryFn: () => getPostV2(ex!.id),
    enabled: needsEvent,
    retry: false,
    gcTime: 0,
  });
  if (!needsEvent) return <PostEditor {...props} />;
  if (full.data?.event) return <PostEditor {...props} existing={full.data} />;
  const failed = full.isError || (full.data != null && full.data.event == null);
  return (
    <div role="dialog" aria-modal="true" aria-label={t("wall_edit_post", "Edit post")} data-testid="composer-loading"
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-[color-mix(in_srgb,var(--foreground)_40%,transparent)]">
      <div className="flex w-[400px] max-w-full flex-col items-center gap-3 rounded-2xl bg-[var(--bg-surface)] p-6 shadow-xl">
        {failed ? (
          <>
            <p role="alert" className="text-sm text-[var(--destructive-text)]">
              {contentErrorText(t, full.error instanceof ContentError ? full.error.code : "UNKNOWN", t("wall_error_UNKNOWN", "Something went wrong — try again"))}
            </p>
            <button type="button" className="h-10 text-sm font-semibold text-[var(--primary)] dark:text-[var(--primary-on-surface)]" onClick={props.onClose}>
              {t("wall_close", "Close")}
            </button>
          </>
        ) : (
          <span aria-busy="true" className="text-sm text-[var(--foreground-muted)]">…</span>
        )}
      </div>
    </div>
  );
}

function PostEditor({ target, existing, source, resolveDelayMs = 500, inPanel = false, initialLocation, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const rules = composerRules(target);
  const personal = target.type === "personal";
  const news = target.type === "news";
  const isEvent = target.type === "event";
  /** News or Event: a v2 post, photos + links only. */
  const typed = isTypedTarget(target);
  const existingV2 = existing as PostV2 | null | undefined;
  const [showOnMaptxat, setShowOnMaptxat] = useState(existingV2?.news?.showOnMaptxat ?? true);
  // Event fields: an edit starts from the stored event (in its own zone, Join me kept as its profile link).
  const [eventDraft, setEventDraft] = useState<EventDraft>(() =>
    existingV2?.event
      ? eventDraftOf(existingV2.event, existingV2.author.id, window.location.origin)
      : { ...newEventDraft(browserTimezone()), coords: initialLocation ? { lat: initialLocation.lat, lng: initialLocation.lng } : null },
  );
  const [joinMeKnown] = useState(() => {
    const e = existingV2?.event;
    return e && existingV2 && e.joinMe.id !== existingV2.author.id ? { url: profileLinkOf(window.location.origin, e.joinMe.id), name: e.joinMe.name, unavailable: !!e.joinMeUnavailable } : null;
  });
  // INVALID_FIELD on save with a pasted Join me link: shown under that field until the link changes.
  const [joinMeRejected, setJoinMeRejected] = useState(false);
  const patchEvent = (p: Partial<EventDraft>) => {
    if (p.joinMeUrl !== undefined) setJoinMeRejected(false);
    setEventDraft((cur) => ({ ...cur, ...p }));
  };
  const eventErrs = isEvent ? eventErrors(eventDraft) : {};
  const panel = inPanel && !personal;
  // In the panel it covers nothing; as a modal it covers the dock.
  useAppOverlay(!panel);
  // A cast that starts while writing (someone else's included) must not land on Add link / Post: the dock
  // re-anchors off the composer (StreamDock) and goes back when it closes.
  useDockAvoid();
  // Phone: the dock sits at the bottom over the panel's foot; pad past it so Post / Cancel scroll clear (pb-6 plus).
  const dockInset = useDockAvoidStore((s) => s.bottomInset);
  // Import: the source post seeds the editor like `existing` does on edit; publishing creates a new
  // post with sourcePostId. Edit never sends sourcePostId, even when a source is passed.
  const importing = !existing && !!source;
  const [prefill] = useState(() => (importing ? prefillText(source, target) : null));
  const [title, setTitle] = useState(existing?.title ?? prefill?.title ?? "");
  const [caption, setCaption] = useState(existing?.caption ?? prefill?.caption ?? "");
  const [items, setItems] = useState<ComposerItem[]>(() =>
    existing ? itemsFromPost(existing.items, target, "edit") : importing ? itemsFromPost(source.items, target, "import") : [],
  );
  // What the draft started as: an edit or import that is still unchanged closes freely.
  const [initialDraft] = useState(() => draftSignature(title, caption, items, typedExtra()));
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // One id per editor session: its videos' queue target, and the exactly-once guard of its post.
  const [draftId] = useState(() => crypto.randomUUID());
  // New videos send through the app-wide upload queue; their rows read its state (Figma Composer item Type=Video).
  const queueItems = useUploadQueueStore((s) => s.items);
  const readyVideos = useWallVideoPostStore((s) => s.ready);
  const view = useMemo(
    () => items.map((it) => (it.kind === "file" && it.uploadId != null ? withVideoSend(it, videoSendOf(queueItems[it.uploadId]), readyVideos[it.uploadId]) : it)),
    [items, queueItems, readyVideos],
  );
  const viewRef = useRef(view);
  viewRef.current = view;
  // A video cancelled from the queue toast leaves the list (as × on its row does).
  useEffect(() => {
    const gone = items.filter((it) => it.kind === "file" && it.uploadId != null && !readyVideos[it.uploadId] && queueItems[it.uploadId]?.phase === "cancelled");
    if (gone.length === 0) return;
    releaseWallVideos(gone.map((it) => (it.kind === "file" ? it.uploadId! : "")));
    const keys = new Set(gone.map((it) => it.key));
    setItems((cur) => cur.filter((it) => !keys.has(it.key)));
  }, [items, queueItems, readyVideos]);
  // Unmounted by anything but Cancel / Discard (a route change, the host closing it) with videos still
  // sending: the post is queued as on close. Discarded (or nothing to queue): the videos still sending stop.
  const discarded = useRef(false);
  useEffect(
    () => () => {
      if (isDraftQueued(draftId)) return;
      if (!discarded.current && queueOnCloseRef.current()) return;
      releaseWallVideos(viewRef.current.flatMap((it) => (it.kind === "file" && it.uploadId != null ? [it.uploadId] : [])));
    },
    [draftId],
  );
  /** A typed post's own fields, for the dirty check. */
  function typedExtra(): string {
    return isEvent ? JSON.stringify(eventDraft) : news ? String(showOnMaptxat) : "";
  }
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const fileInput = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLElement>(null);

  // Caption limit in code points (the server counts runes), behaving like a native maxLength:
  // only the inserted run is cut, and nothing is rewritten while an IME composition is open.
  const captionRef = useRef<HTMLTextAreaElement>(null);
  const composing = useRef<string | null>(null); // the caption before the open composition
  const [captionCaret, setCaptionCaret] = useState<{ at: number } | null>(null);
  function acceptCaption(prev: string, el: HTMLTextAreaElement) {
    const r = clampInsertion(prev, el.value, el.selectionStart ?? el.value.length, rules.captionMax);
    setCaption(r.value);
    if (r.value !== el.value) setCaptionCaret({ at: r.caret });
  }
  useLayoutEffect(() => {
    if (captionCaret) captionRef.current?.setSelectionRange(captionCaret.at, captionCaret.at);
  }, [captionCaret]);

  useEffect(() => {
    const map = timers.current;
    return () => map.forEach(clearTimeout);
  }, []);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Closing while new videos still send, with nothing else to fix, queues the post (spec 2026-10-23 web):
  // it is created once every video is READY, even after the editor is gone. Set every render below.
  const queueOnCloseRef = useRef<() => boolean>(() => false);
  /** The close button: queues the post when it can, else closes (a draft not queued is dropped). */
  const closeNow = () => {
    queueOnCloseRef.current();
    onCloseRef.current();
  };
  // Escape and an outside tap close a clean draft; a dirty one asks first (the host's tab switch too).
  // A draft that can be queued closes at once: nothing is lost.
  const guardedClose = () => {
    if (queueOnCloseRef.current()) onCloseRef.current();
    else
      useComposerDraftStore.getState().guard(() => {
        discarded.current = true;
        onCloseRef.current();
      });
  };
  const requestClose = guardedClose;
  const guardedCloseRef = useRef(guardedClose);
  guardedCloseRef.current = guardedClose;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // A keyboard drag in the list cancels on Escape (dnd-kit prevents the default), and so does the open
      // discard dialog; only a free Escape closes.
      if (e.key === "Escape" && !e.defaultPrevented) guardedCloseRef.current();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    return () => previous?.focus?.();
  }, []);

  const patch = (key: number, p: Partial<ComposerItem>) => setItems((cur) => patchItem(cur, key, p));
  /** Update the link with `key` from its current state (never from a stale copy). */
  const updateLink = (key: number, f: (it: LinkItem) => LinkItem) =>
    setItems((cur) => cur.map((it) => (it.key === key && it.kind === "link" ? f(it) : it)));

  function onUrlChange(key: number, url: string) {
    patch(key, { url, status: url.trim() ? "checking" : "idle", preview: undefined, error: undefined });
    clearTimeout(timers.current.get(key));
    if (!url.trim()) return;
    timers.current.set(
      key,
      setTimeout(async () => {
        // A result is applied only while the row still holds the URL it was asked for: a stale
        // in-flight resolve must never mark the row ok, nor send the old URL.
        const current = (it: LinkItem) => it.url === url;
        try {
          const preview = await resolveLink(url.trim());
          updateLink(key, (it) => (current(it) ? applyResolved(it, preview) : it));
        } catch (e) {
          const code = e instanceof ContentError ? e.code : "UNKNOWN";
          updateLink(key, (it) => (current(it) ? { ...it, status: "error", preview: undefined, error: code } : it));
        }
      }, resolveDelayMs),
    );
  }

  // Import: links are resolved again (they may have gone away since the source was posted).
  useEffect(() => {
    if (!importing) return;
    items.forEach((it) => it.kind === "link" && it.status !== "error" && onUrlChange(it.key, it.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onFiles(files: FileList | null) {
    if (!files) return;
    // The cap is enforced here: picks beyond MAX_ITEMS are dropped.
    const tiles = filesFromPicked(Array.from(files), MAX_ITEMS - items.length).map((tile) =>
      // News and Event take photos only (a video needs the queued v1 wall path): a video is refused as an unsupported type.
      typed && isVideoItem(tile) ? { ...tile, status: "error" as const, error: "video_type_not_supported", file: undefined }
        // A personal (v1 photo) wall refuses audio, as it refuses links.
        : personal && tile.type === "AUDIO" ? { ...tile, status: "error" as const, error: "wall_audio_not_allowed", file: undefined }
        // A video goes to the app-wide queue at once (compress, then upload); its row follows the queue.
        : isVideoItem(tile) && tile.file ? { ...tile, file: undefined, uploadId: enqueueWallVideo(tile.file, draftId, tile.key) } : tile,
    );
    setItems((cur) => [...cur, ...tiles.map(({ file: _f, ...tile }) => tile)]);
    for (const tile of tiles) {
      if (!tile.file) continue;
      try {
        if (tile.type === "AUDIO") {
          // No preview picture: the row shows the audio icon.
          const res = await uploadAudio(tile.file);
          patch(tile.key, { status: "done", fileId: res.id });
          continue;
        }
        const res = await uploadImage(tile.file);
        patch(tile.key, { status: "done", fileId: res.id, previewUrl: res.url });
      } catch (err) {
        patch(tile.key, { status: "error", error: err instanceof SvgTooLargeError ? "image_svg_too_large" : "wall_photo_upload_failed" });
      }
    }
  }

  const full = items.length >= MAX_ITEMS;
  const blocker = blockerOf(view, caption, rules, title);
  const videoIds = view.flatMap((it) => (it.kind === "file" && it.uploadId != null ? [it.uploadId] : []));

  /** The request for these items (create, import or edit), as it is when called. */
  function inputFor(postItems: PostItemInput[]): PostInput {
    return {
      kind: personal ? "PHOTO" : deriveKind(view),
      ...(rules.titleField ? { title } : {}),
      caption,
      items: postItems,
      ...(importing && source ? { sourcePostId: source.id } : {}),
    };
  }
  /** The v2 body (create and PUT v2 alike): the type is the target's, never changed by an edit. */
  function v2Input(input: PostInput): PostInputV2 {
    const base = { title: input.title ?? "", description: input.caption, items: input.items };
    return isEvent ? { type: "EVENT", ...base, event: toEventInput(eventDraft) } : { type: "NEWS", ...base, news: { showOnMaptxat } };
  }
  const send = (input: PostInput): Promise<WallPost> => {
    if (typed) {
      const body = v2Input(input);
      if (existing) return updatePostV2(existing.id, body);
      if (body.type === "EVENT" && body.event) {
        const { type: _t, ...rest } = body;
        return createEvent({ ...rest, event: body.event });
      }
      return createPostV2(body);
    }
    return existing ? updatePost(existing.id, input) : target.type === "gc" ? createPost(target.convId, input) : createOnMyWall(input);
  };

  queueOnCloseRef.current = () => {
    if (submitting || !canQueuePost(view, caption, rules, title)) return false;
    const base = inputFor([]);
    const gc = target.type === "gc" ? target.convId : null;
    queueWallPost({
      draftId,
      items: view,
      save: async (postItems) => {
        const saved = await send({ ...base, items: postItems });
        // The editor is gone: refresh the feeds it would have (the host's onSaved does it for a direct post).
        if (gc != null) upsertPost(qc, { type: "gc", convId: gc }, saved);
        else if (typed) invalidateNewsPosted(qc);
        else invalidatePersonalFeeds(qc);
        return saved;
      },
    });
    return true;
  };

  async function submit() {
    // Exactly once per draft: a queued copy of this draft, or a double tap, never posts it again.
    if (!claimDraft(draftId)) return;
    const input = inputFor(toPostItems(view));
    setSubmitting(true);
    setSubmitError(null);
    try {
      const saved = await send(input);
      // News / Event: the General Wall, the events lists and agenda (all under "wallFeed"), post lists and counts.
      if (typed) invalidateNewsPosted(qc);
      else if (personal) invalidatePersonalFeeds(qc);
      // The videos are in the post: their queue items (and local posters) are done.
      releaseWallVideos(videoIds);
      onSaved(saved);
    } catch (e) {
      releaseDraft(draftId);
      const code = e instanceof ContentError ? e.code : "UNKNOWN";
      // An event's other fields are checked before sending, so INVALID_FIELD with a newly pasted Join me link is that
      // link (room-stc names no field): shown under it, the input kept. The unchanged prefilled link was accepted
      // before, so then the error is someone else's: the generic save error.
      const link = eventDraft.joinMeUrl.trim();
      if (isEvent && code === "INVALID_FIELD" && link !== "" && link !== joinMeKnown?.url) setJoinMeRejected(true);
      else setSubmitError(importing && code === "NOT_FOUND" ? "SOURCE_NOT_FOUND" : code);
    } finally {
      setSubmitting(false);
    }
  }

  const heading = importing
    ? t("wall_import_post_heading", "Import post")
    : existing
      ? t("wall_edit_post", "Edit post")
      : news
        ? t("composer_news_title", "News")
        : isEvent
          ? t("composer_event_title", "Event")
          : t("wall_new_personal_post", "New post");
  const primaryLabel =
    blocker?.kind === "uploading"
      ? t("wall_uploading", { done: blocker.done, total: blocker.total, defaultValue: "Uploading {{done}} of {{total}}…" })
      : blocker?.kind === "failedVideos"
        ? t("wall_editor_fix_videos", { count: blocker.count, defaultValue: "Fix {{count}} video(s) to post" })
      : blocker?.kind === "failedFiles"
        ? t("wall_photos_fix", "Remove the photos that failed to post")
        : blocker?.kind === "links"
          ? t("wall_fix_links", { count: blocker.count, defaultValue_one: "Fix {{count}} link to post", defaultValue_other: "Fix {{count}} links to post" })
          : blocker?.kind === "titles"
            ? t("wall_fix_titles", { count: blocker.count, defaultValue_one: "Fix {{count}} title to post", defaultValue_other: "Fix {{count}} titles to post" })
            : existing ? t("wall_save_button", "Save") : t("wall_post_button", "Post");
  const canPost = !blocker && !submitting && Object.keys(eventErrs).length === 0;
  // Changed text or items, or an upload in flight: a stray tap, Escape or a tab switch asks before discarding it.
  const dirty = view.some((it) => it.kind === "file" && it.status === "uploading") || draftSignature(title, caption, items, typedExtra()) !== initialDraft;
  const pendingDiscard = useComposerDraftStore((s) => s.pending);
  useEffect(() => {
    useComposerDraftStore.getState().set({ dirty });
  }, [dirty]);
  useEffect(() => {
    useComposerDraftStore.getState().set({ inPanel: panel, requestClose: () => guardedCloseRef.current() });
    return () => useComposerDraftStore.getState().reset();
  }, [panel]);
  const discardDialog =
    pendingDiscard &&
    createPortal(
      // Portalled, but React events still bubble to the modal's backdrop: keep its clicks here.
      <div style={{ display: "contents" }} onClick={(e) => e.stopPropagation()}>
      <LiveConfirmDialog
        testId="discard-draft-dialog"
        zClass="z-[2200]"
        message={t("wall_discard_draft_message", "Discard this post? Your changes will be lost.")}
        confirmLabel={t("wall_discard_draft_confirm", "Discard")}
        cancelLabel={t("wall_discard_draft_keep", "Keep editing")}
        confirmTestId="discard-draft-confirm"
        onConfirm={() => useComposerDraftStore.getState().confirmDiscard()}
        onCancel={() => useComposerDraftStore.getState().cancelDiscard()}
      />
      </div>,
      document.body,
    );

  const fieldClass =
    "rounded-xl bg-[var(--bg-elevated)] px-3 py-3 text-base text-[var(--foreground)] outline-none placeholder:text-[var(--foreground-subtle)] focus-visible:ring-2 focus-visible:ring-[var(--primary-text)]";
  const pillClass =
    "h-12 flex-1 rounded-full border border-[var(--border-strong)] bg-[var(--bg-surface)] text-sm font-semibold text-[var(--foreground)] disabled:opacity-45";
  const primaryButton = (extra: string) => (
    <button type="button" disabled={!canPost} onClick={submit}
      className={`h-12 rounded-full bg-[var(--primary)] font-semibold text-[var(--primary-foreground)] disabled:opacity-45 ${extra}`}>
      {primaryLabel}
    </button>
  );
  const cancelButton = (extra: string) => (
    <button type="button" onClick={() => { discarded.current = true; onClose(); }} className={`h-10 text-sm font-semibold text-[var(--primary)] dark:text-[var(--primary-on-surface)] ${extra}`}>{t("wall_cancel", "Cancel")}</button>
  );

  const body = (
    <>
        <header className="flex items-center gap-2 pt-4">
          {!personal && (importing ? <FolderUp size={22} className="text-[var(--primary)] dark:text-[var(--primary-on-surface)]" aria-hidden /> : <SquarePen size={22} className="text-[var(--primary)] dark:text-[var(--primary-on-surface)]" aria-hidden />)}
          <h2 className="flex-1 text-lg font-bold text-[var(--foreground)]">{heading}</h2>
          <button type="button" aria-label={t("wall_close", "Close")} className="text-[var(--foreground-muted)]" onClick={closeNow}>
            <X size={22} aria-hidden />
          </button>
        </header>

        {importing && source && (
          <p className="-mt-1 text-xs text-[var(--foreground-muted)]">
            {t("wall_import_note", {
              title: uploadTitle(source) || t("my_uploads_untitled", "Untitled"),
              defaultValue: "From “{{title}}” · add, remove or reorder. The original post is not changed.",
              interpolation: { escapeValue: false },
            })}
          </p>
        )}

        {rules.titleField && (
          <label className="flex flex-col gap-1.5 text-xs font-medium text-[var(--foreground-muted)]">
            {t("wall_title_label", "Title")}
            <input data-autofocus className={fieldClass} maxLength={MAX_TITLE} value={title}
              placeholder={t("wall_title_placeholder", "Add a title")} onChange={(e) => setTitle(e.target.value)} />
          </label>
        )}
        <label className="flex flex-col gap-1.5 text-xs font-medium text-[var(--foreground-muted)]">
          {t("wall_caption_label", "Caption")}
          <textarea
            {...(personal ? { "data-autofocus": true } : {})}
            className={`${fieldClass} resize-none [field-sizing:content] max-h-48`}
            rows={1}
            ref={captionRef}
            value={caption}
            placeholder={t("wall_caption_placeholder_mixed", "Say something about this post")}
            onChange={(e) => (composing.current !== null ? setCaption(e.target.value) : acceptCaption(caption, e.target))}
            onCompositionStart={() => {
              composing.current = caption;
            }}
            onCompositionEnd={(e) => {
              const before = composing.current ?? caption;
              composing.current = null;
              acceptCaption(before, e.currentTarget);
            }}
          />
        </label>
        {personal && (
          <span data-testid="caption-counter" className="-mt-2 self-end text-[11px] text-[var(--foreground-subtle)]">
            {t("wall_caption_counter", { count: codePointCount(caption), max: rules.captionMax, defaultValue: "{{count}} / {{max}}" })}
          </span>
        )}

        <ComposerItemList
          items={view}
          personal={personal}
          onReorder={setItems}
          onRemove={(key) => {
            clearTimeout(timers.current.get(key));
            timers.current.delete(key);
            // A new video: × cancels its send (encode, then the multipart upload) and frees its queue item.
            const gone = items.find((it) => it.key === key);
            if (gone?.kind === "file" && gone.uploadId != null) releaseWallVideos([gone.uploadId]);
            setItems((cur) => cur.filter((it) => it.key !== key));
          }}
          onRetry={(key) => {
            const it = items.find((x) => x.key === key);
            if (it?.kind === "file" && it.uploadId != null) uploadQueue.retry(it.uploadId);
          }}
          onUrlChange={onUrlChange}
          onTitleChange={(key, x) => updateLink(key, (it) => editTitle(it, x))}
          onTitleReset={(key) => updateLink(key, resetTitle)}
          onCaptionChange={news ? (key, caption) => patch(key, { caption }) : undefined}
        />

        <div className="flex gap-2">
          <button type="button" disabled={full} className={pillClass} onClick={() => fileInput.current?.click()}>
            {typed ? t("wall_add_photos", "Add photos") : t("wall_editor_add_media", "Add photos or videos")}
          </button>
          {rules.links && (
            <button type="button" disabled={full} className={pillClass} onClick={() => setItems((cur) => (cur.length >= MAX_ITEMS ? cur : [...cur, newLink()]))}>
              {t("wall_add_link", "Add link")}
            </button>
          )}
        </div>
        <p className="-mt-1 text-xs text-[var(--foreground-subtle)]">{typed ? t("wall_photos_audio_hint", "Photos and MP3 · JPG, PNG, WebP or MP3") : personal ? t("wall_editor_media_hint", "Photos and videos · JPG, PNG, MP4, MOV or WebM") : t("wall_editor_media_audio_hint", "Photos, videos and MP3 · JPG, PNG, MP4, MOV, WebM or MP3")}</p>
        <input data-testid="photo-input" ref={fileInput} type="file" accept={(typed ? [...PHOTO_TYPES, ...AUDIO_TYPES] : personal ? MEDIA_TYPES : [...MEDIA_TYPES, ...AUDIO_TYPES]).join(",")} multiple hidden
          onChange={(e) => { const picked = e.target.files; void onFiles(picked); e.target.value = ""; }} />

        {isEvent && (
          <EventFields draft={eventDraft} onChange={patchEvent} errors={eventErrs} joinMeRejected={joinMeRejected} joinMeKnown={joinMeKnown} />
        )}

        {news && (
          <label className="flex items-center justify-between gap-3 text-sm text-[var(--foreground)]">
            {t("composer_news_maptxat", "Show on my MapTxat")}
            <input data-testid="composer-news-maptxat" type="checkbox" role="switch" checked={showOnMaptxat}
              onChange={(e) => setShowOnMaptxat(e.target.checked)} className="h-5 w-5 accent-[var(--primary)]" />
          </label>
        )}

        {submitError && (
          <p role="alert" className="text-sm text-[var(--destructive)]">
            {submitError === "SOURCE_NOT_FOUND"
              ? t("wall_error_SOURCE_NOT_FOUND", "This upload is no longer available")
              : contentErrorText(t, submitError, t("wall_error_UNKNOWN", "Something went wrong — try again"))}
          </p>
        )}

        {personal ? (
          <div className="mt-2 flex items-center justify-end gap-4">
            {cancelButton("px-2")}
            {primaryButton("px-7")}
          </div>
        ) : (
          <div className="mt-2 flex flex-col gap-1">
            {primaryButton("w-full")}
            {cancelButton("")}
          </div>
        )}
    </>
  );

  if (panel) {
    // Fills the wall panel under the app header (Figma 4260:3761 / 4260:3849): no backdrop, the host scrolls.
    return (
      <section ref={dialogRef} role="dialog" aria-label={heading} data-testid="composer-panel"
        className="flex min-h-full flex-col gap-3 bg-[var(--bg-surface)] px-4 pb-6"
        style={dockInset > 0 ? { paddingBottom: `calc(1.5rem + ${dockInset}px + env(safe-area-inset-bottom, 0px))` } : undefined}>
        {body}
        {discardDialog}
      </section>
    );
  }
  return (
    <div ref={dialogRef as React.RefObject<HTMLDivElement>} role="dialog" aria-modal="true" aria-label={heading} data-testid="composer-modal"
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-[color-mix(in_srgb,var(--foreground)_40%,transparent)]"
      onClick={requestClose}>
      <div className="flex max-h-[90vh] w-[400px] max-w-full flex-col gap-3 overflow-y-auto rounded-2xl bg-[var(--bg-surface)] px-4 pb-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}>
        {body}
      </div>
      {discardDialog}
    </div>
  );
}

/** The parts of a draft the user changes (not resolve results or upload progress). */
function draftSignature(title: string, caption: string, items: ComposerItem[], typed = ""): string {
  return JSON.stringify([
    title.trim(),
    caption.trim(),
    typed,
    items.map((it) => (it.kind === "file" ? [it.key, it.fileId ?? null, it.caption ?? ""] : [it.key, it.url.trim(), it.titleEdited ? it.title : null, it.caption ?? ""])),
  ]);
}
